/**
 * Standalone signup page.
 *
 * Asks for what a new tenant needs — company name, email, phone, password —
 * then proves the email with a one-time code. The phone is *not* verified: it
 * is contact information, and making the user wait on an SMS would only stand
 * between them and the onboarding wizard, which is where this page lands them
 * once the account and its company exist.
 *
 * **Unless they are joining one that exists.** Someone invited owes us no
 * company name — the field is *gone*, not optional, no tenant is created and
 * the onboarding is skipped: they go to the invitation. Everyone else must
 * still name their company, which is what they came here to open.
 *
 * We know which of the two only once the address is proven, so the question is
 * asked at the two moments it can be (`AuthAPI.signupInvitations`, which takes
 * the email pre-signed token as the proof):
 * - **on arrival**, when the token is handed over — from the login form or the
 *   "Accept the invitation" page, both of which verify the email before
 *   sending anyone here. The form then renders without the company field.
 * - **after the code**, for someone who came straight to `/signup` and filled
 *   the form first. The company they typed is then dropped: they are joining a
 *   team, and a tenant nobody asked for is exactly what this avoids.
 */

import { AuthLayout } from "@/components/molecules/auth-layout";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { AuthAPI } from "@/features/auth/api/auth";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useCreateClient } from "@/features/clients/hooks/use-clients";
import { getCaptchaToken, useGReCaptcha } from "@/features/utils/captcha";
import { useTranslation } from "@/features/utils/i18n";
import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { useTitle } from "@/features/utils/use-title";
import { useRef, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AccountForm,
  type JoinedCompany,
  type SignupDetails,
} from "./components/account-form";
import { VerifyEmail } from "./components/verify-email";

type Step = "form" | "otp" | "creating";

export const SignupPage = () => {
  useGReCaptcha();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isInitializing, isAuthenticated } = useAuth();
  const createClient = useCreateClient();

  // The login form and the invitation page send users here when their email has
  // no account yet — with the code already verified, so we must not ask for a
  // second one.
  const prefilled = (location.state || {}) as {
    email?: string;
    emailPreSignedToken?: string;
    invited?: boolean;
  };
  // Also in the query string: the state is lost on a reload, and the token with
  // it, so this is what still says "this one was invited" on the way back.
  const [searchParams] = useSearchParams();
  const invited = !!prefilled.invited || searchParams.get("invited") === "1";

  const [step, setStep] = useState<Step>("form");
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState<SignupDetails>({
    companyName: "",
    email: prefilled.email || "",
    phone: "",
    password: "",
    fullName: "",
  });
  const [verifiedToken, setVerifiedToken] = useState(
    prefilled.emailPreSignedToken || "",
  );
  // The company this address was invited to, once we have been able to ask.
  const [joining, setJoining] = useState<JoinedCompany | null>(null);
  // Set from the moment we log the new account in, so the "already signed in"
  // redirect below does not race our own navigation to the onboarding.
  const isSigningUp = useRef(false);

  // Joining a team, by any of the three ways we can know it: the invitation we
  // found, the page that sent us here, or a reload of that page's URL.
  const isJoining = !!joining || invited;
  useTitle(t(isJoining ? "signup.invitedTitle" : "signup.title"));

  useControlledEffect(() => {
    if (isInitializing || isSigningUp.current) return;
    if (isAuthenticated)
      navigate(isJoining ? "/invitations" : "/start", { replace: true });
  }, [isInitializing, isAuthenticated]);

  /** The invitations waiting for a proven address; none on any failure. */
  const findInvitation = async (emailPreSignedToken: string) => {
    const result = await AuthAPI.signupInvitations({
      emailPreSignedToken,
    }).catch((error) => {
      console.error(error);
      return { invitations: [] as JoinedCompany[] };
    });
    const invitation = result.invitations[0] || null;
    setJoining(invitation);
    return invitation;
  };

  // Arrived with the email already proven: ask now, so the form below is drawn
  // with or without its company field rather than correcting itself later.
  useControlledEffect(() => {
    if (!prefilled.emailPreSignedToken) return;
    findInvitation(prefilled.emailPreSignedToken);
  }, [prefilled.emailPreSignedToken]);

  // The phone is never verified, but a number nobody could call back is a typo
  // rather than a contact: the backend rejects it, so catch it here. Someone
  // joining a team owes us neither a company nor a phone — only a way to sign
  // in; someone opening their own still owes us its name.
  const isValid =
    (isJoining || !!details.companyName.trim()) &&
    /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(details.email.trim()) &&
    (isJoining
      ? !details.phone || isValidPhoneNumber(details.phone)
      : isValidPhoneNumber(details.phone)) &&
    details.password.length >= 8;

  const createAccount = async (
    emailPreSignedToken: string,
    // Passed explicitly by the code step, which has just looked it up: the
    // state it also set is not readable from this same tick.
    invitation: JoinedCompany | null = joining,
  ) => {
    // Joining a team drops the company name — including one typed before we
    // could know, on the path where the form comes first.
    const joiningTeam = !!invitation || invited;
    const companyName = joiningTeam ? "" : details.companyName.trim();

    setIsLoading(true);
    setStep("creating");
    try {
      // The name is one field on the form; the API takes it in two, and the
      // member list shows the first word.
      const [firstName, ...lastName] = (details.fullName || "")
        .trim()
        .split(/\s+/);
      const result = await AuthAPI.signup({
        emailPreSignedToken,
        companyName: companyName || undefined,
        phone: details.phone || undefined,
        firstName: firstName || undefined,
        lastName: lastName.join(" ") || undefined,
        password: details.password,
      });
      if (!result.success || !result.preSignedToken) {
        throw new Error(t("signup.failed"));
      }

      isSigningUp.current = true;
      // The account was just created with a password; the security-key prompt
      // would otherwise sit between the user and their onboarding.
      localStorage.setItem("webauthn_skipped", "true");
      // signup returns "<emailToken>:<passwordToken>" — login needs both.
      await login(result.preSignedToken.split(":"));
    } catch (error) {
      console.error(error);
      isSigningUp.current = false;
      setStep("form");
      toast.error(error instanceof Error ? error.message : t("signup.failed"));
      return;
    } finally {
      setIsLoading(false);
    }

    // From here the user exists and is logged in: a failure must land them
    // somewhere usable, never back on a form they already submitted.

    // Joining a team: the invitation is the next step, and the onboarding
    // wizard is not — the company they are joining is already set up.
    if (joiningTeam) {
      toast.success(t("signup.success"));
      navigate("/invitations", { replace: true });
      return;
    }

    if (!companyName) {
      // Nothing to create and nothing to join: the landing page offers both.
      toast.success(t("signup.success"));
      navigate("/start", { replace: true });
      return;
    }

    try {
      const created = await createClient.mutateAsync({ name: companyName });
      toast.success(t("signup.success"));
      navigate(`/${created.client.id}/onboarding`, { replace: true });
    } catch (error) {
      console.error(error);
      toast.error(t("start.createCompany.failed"));
      navigate("/start", { replace: true });
    }
  };

  const sendCode = async () => {
    await AuthAPI.emailChallenge({
      email: details.email.trim(),
      captchaToken: await getCaptchaToken("email_challenge"),
    });
    toast.success(t("email.codeSent", { email: details.email.trim() }));
  };

  const handleDetailsSubmit = async () => {
    if (!isValid) return;

    // Already proven (came from the login form, or stepped back after typing
    // the code): go straight to account creation.
    if (verifiedToken) {
      await createAccount(verifiedToken);
      return;
    }

    setIsLoading(true);
    try {
      const check = await AuthAPI.passwordCheck({
        email: details.email.trim(),
      });
      if (check.success) {
        // Known account with a password: signing up again cannot succeed.
        toast.error(t("signup.emailAlreadyUsed"));
        return;
      }

      await sendCode();
      setStep("otp");
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : t("email.sendFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otp: string) => {
    if (otp.length !== 6) return;

    let token = "";
    setIsLoading(true);
    try {
      const result = await AuthAPI.emailVerify({
        email: details.email.trim(),
        otp,
      });
      if (!result.success || !result.preSignedToken) {
        throw new Error(t("otp.invalid"));
      }

      if ((result.otherFactors || []).length > 0) {
        // The address already carries an account — signing in is what they
        // actually need, and the signup call would only fail.
        toast.info(t("signup.emailAlreadyUsed"));
        navigate("/", { replace: true });
        return;
      }

      token = result.preSignedToken;
      setVerifiedToken(token);
    } catch (error) {
      // A wrong code is worth retyping: stay on this step.
      console.error(error);
      toast.error(error instanceof Error ? error.message : t("otp.invalid"));
      return;
    } finally {
      setIsLoading(false);
    }

    // The address is proven only now, on this path: this is the first moment we
    // can tell an invited member from someone opening their own company — and
    // the last one before the account (and maybe a tenant) is created.
    const invitation = await findInvitation(token);
    await createAccount(token, invitation);
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await sendCode();
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : t("email.sendFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex h-dvh w-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <AuthLayout>
      <CardHeader className="-mb-4">
        <CardTitle className="text-2xl font-semibold">
          {isJoining ? t("signup.invitedTitle") : t("signup.title")}
        </CardTitle>
      </CardHeader>

      {step === "creating" ? (
        <div className="flex w-full justify-center py-8">
          <Spinner />
        </div>
      ) : step === "otp" ? (
        <VerifyEmail
          email={details.email.trim()}
          isLoading={isLoading}
          onVerify={handleOtpVerify}
          onResend={handleResend}
          onBack={() => setStep("form")}
        />
      ) : (
        <AccountForm
          values={details}
          onChange={setDetails}
          onSubmit={handleDetailsSubmit}
          isLoading={isLoading}
          isValid={isValid}
          emailLocked={!!verifiedToken}
          joining={isJoining}
          joiningCompany={joining}
        />
      )}
    </AuthLayout>
  );
};
